/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

import format from "string-format";
import globals from "modules/globals";
import { djangoGetText } from "./django-translation-catalog";
/**
 * These methods wrap Django i18n methods in an es6 module,
 * while adding you support for python style formatting of i18n strings that have placeholders
 * and handling the case where the django methods are not available - for example during JS testing.
 * Documentation is copied from https://docs.djangoproject.com/en/1.11/topics/i18n/translation/#gettext
 */

/**
 * The gettext function behaves similarly to the standard gettext interface within your Python code
 * with an additional optional argument to format the string with placeholder values.
 *
 * @example
 *  document.write(gettext('this is to be translated'));
 *
 * @param {String} msgid
 * @param {Object} placeholder names and values to format into the string
 * @returns {String} a locally translated string
 */
export function gettext(msgid, formatPlaceholders = null) {
    if (globals.window.django) {
        // Production behaviour
        let content = djangoGetText(msgid);
        if (formatPlaceholders) {
            return format(content, formatPlaceholders);
        } else {
            return content;
        }
    } else {
        // Fallback behaviour when running JS Tests
        return msgid;
    }
}

/**
 * The ngettext function provides an interface to pluralize words and phrases
 * with an additional optional argument to format the string with placeholder values.
 *
 * @example
 *  var objectCount = 1 // or 0, or 2, or 3, ...
 *  s = ngettext('literal for the singular case', 'literal for the plural case', objectCount);
 *
 * @param {String} msgidSingular
 * @param {String} msgidPlural
 * @param {Number} objectCount
 * @param {Object} placeholder names and values to format into the string
 * @returns {String} a locally translated string
 */
export function ngettext(msgidSingular, msgidPlural, objectCount, formatPlaceholders = null) {
    if (globals.window.django) {
        // Production behaviour
        let content = globals.window.django.ngettext(msgidSingular, msgidPlural, objectCount);
        if (formatPlaceholders) {
            return format(content, formatPlaceholders);
        } else {
            return content;
        }
    } else if (objectCount === 1) {
        // Fallback behaviour when running JS Tests
        return msgidSingular;
    } else {
        // Fallback behaviour when running JS Tests
        return msgidPlural;
    }
}

/**
 * The interpolate function supports dynamically populating a format string.
 * The interpolation syntax is borrowed from Python, so the interpolate function supports both positional
 * and named interpolation
 *
 * Positional interpolation: obj contains a JavaScript Array object whose elements values are then sequentially
 * interpolated in their corresponding fmt placeholders in the same order they appear. For example:
 *
 * @example
 *  fmts = ngettext('There is %s object. Remaining: %s',
 *          'There are %s objects. Remaining: %s', 11);
 *  s = interpolate(fmts, [11, 20]);
 *  // s is 'There are 11 objects. Remaining: 20'
 *
 * Named interpolation: This mode is selected by passing the optional boolean named parameter as true.
 * obj contains a JavaScript object or associative array. For example:
 *
 * @example
 *  d = {
 *      count: 10,
 *      total: 50
 *  };
 *
 *  fmts = ngettext('Total: %(total)s, there is %(count)s object',
 *  'there are %(count)s of a total of %(total)s objects', d.count);
 *  s = interpolate(fmts, d, true);
 *
 * @param {String} fmt - string to format
 * @param {Object|Array<String>} obj - values to format with
 * @param {Boolean} named - is obj an Object? If not it's an array.
 * @returns {String} an interpolated string
 */
export function interpolate(fmt, obj, named = false) {
    if (globals.window.django) {
        // Production behaviour
        return globals.window.django.interpolate(fmt, obj, named);
    } else {
        // Fallback behaviour when running JS Tests
        return fmt;
    }
}

/**
 * The get_format function has access to the configured i18n formatting settings and can retrieve the format string
 * for a given setting name
 *
 * @example
 *  document.write(get_format('DATE_FORMAT'));
 *  // 'N j, Y'
 *
 * It has access to the following settings:
 *
 * DATE_FORMAT
 * DATE_INPUT_FORMATS
 * DATETIME_FORMAT
 * DATETIME_INPUT_FORMATS
 * DECIMAL_SEPARATOR
 * FIRST_DAY_OF_WEEK
 * MONTH_DAY_FORMAT
 * NUMBER_GROUPING
 * SHORT_DATE_FORMAT
 * SHORT_DATETIME_FORMAT
 * THOUSAND_SEPARATOR
 * TIME_FORMAT
 *
 * @param {String} formatType
 * @returns {String} format for this locale
 */
export function getFormat(formatType) {
    if (globals.window.django) {
        // Production behaviour
        return globals.window.django.get_format(formatType);
    } else {
        // Fallback behaviour when running JS Tests
        return formatType;
    }
}

/**
 * The pgettext function behaves like the Python variant (pgettext()), providing a contextually translated word
 *
 * @example
 *  document.write(pgettext('month name', 'May'));
 *
 * @param {String} context
 * @param {String} msgid
 * @returns {String} a locally translated string
 */
export function pgettext(context, msgid) {
    if (globals.window.django) {
        // Production behaviour
        return globals.window.django.pgettext(context, msgid);
    } else {
        // Fallback behaviour when running JS Tests
        return msgid;
    }
}

/**
 * The npgettext function also behaves like the Python variant (npgettext()), providing a pluralized contextually
 * translated word
 *
 * @example
 *   document.write(npgettext('group', 'party', 1));
 *   // party
 *   document.write(npgettext('group', 'party', 2));
 *   // parties
 *
 * @param {String} context
 * @param {String} msgidSingular
 * @param {String} msgidPlural
 * @param {Number} objectCount
 * @returns {String} a locally translated string
 */
export function npgettext(context, msgidSingular, msgidPlural, objectCount) {
    if (globals.window.django) {
        // Production behaviour
        return globals.window.django.npgettext(context, msgidSingular, msgidPlural, objectCount);
    } else if (objectCount === 1) {
        // Fallback behaviour when running JS Tests
        return msgidSingular;
    } else {
        // Fallback behaviour when running JS Tests
        return msgidPlural;
    }
}

/**
 * The pluralidx function works in a similar way to the pluralize template filter, determining if a given count should
 * use a plural form of a word or not:
 *
 * @example
 *  document.write(pluralidx(0));
 *  // true
 *  document.write(pluralidx(1));
 *  // false
 *  document.write(pluralidx(2));
 *  // true
 *
 * It has access to the following settings:
 *
 * In the simplest case, if no custom pluralization is needed, this returns false for the integer 1 and true
 * for all other numbers.
 *
 * However, pluralization is not this simple in all languages. If the language does not support pluralization,
 * an empty value is provided.
 *
 * Additionally, if there are complex rules around pluralization, the catalog view will render a conditional expression.
 * This will evaluate to either a true (should pluralize) or false (should not pluralize) value.
 *
 * @param {Number} objectCount
 * @returns {Boolean} true (should pluralize) or false (should not pluralize)
 */
export function pluralidx(objectCount) {
    if (globals.window.django) {
        // Production behaviour
        return globals.window.django.pluralidx(objectCount);
    } else if (objectCount === 1) {
        // Fallback behaviour when running JS Tests
        return false;
    } else {
        // Fallback behaviour when running JS Tests
        return true;
    }
}

